/**
 * QO Bottom Bracket Finder
 * Finds the exact DUB-compatible bottom bracket for a customer's bike.
 * Markup + result templates rendered by sections/qo-bb-finder.liquid.
 */
(function () {
  'use strict';

  var MAX_RESULTS = 8;
  var MAX_BRAND_CHIPS = 8;

  function normalize(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s.-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function trackResult(detail) {
    try {
      document.dispatchEvent(new CustomEvent('qo:bbf:result', { detail: detail }));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'qo_bb_finder_result',
        bbf_standard: detail.standard,
        bbf_bike: detail.bike || null,
        bbf_source: detail.source
      });
    } catch (e) { /* analytics must never break the finder */ }
  }

  function BBFinder(root) {
    this.root = root;
    this.databaseUrl = root.dataset.databaseUrl;
    this.cartAddUrl = root.dataset.cartAddUrl || '/cart/add';
    this.cartUrl = root.dataset.cartUrl || '/cart';
    this.database = null;
    this.databasePromise = null;

    this.searchInput = root.querySelector('[data-bbf-search]');
    this.resultsList = root.querySelector('[data-bbf-search-results]');
    this.noResults = root.querySelector('[data-bbf-no-results]');
    this.brandsWrap = root.querySelector('[data-bbf-brands]');
    this.brandChips = root.querySelector('[data-bbf-brand-chips]');
    this.resultArea = root.querySelector('[data-bbf-result-area]');
    this.machine = root.querySelector('[data-bbf-machine]');
    this.category = 'all';

    this.bindTabs();
    this.bindCategoryFilter();
    this.bindSearch();
    this.bindWizard();
    this.bindStandardButtons();

    // Load the database early: it is tiny and first paint of results matters.
    this.loadDatabase();
  }

  /* ---------------- Database ---------------- */

  BBFinder.prototype.loadDatabase = function () {
    var self = this;
    if (this.databasePromise) return this.databasePromise;
    this.databasePromise = fetch(this.databaseUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('BB database HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        self.database = data;
        self.renderBrandChips();
        return data;
      })
      .catch(function (err) {
        console.warn('[QO BB Finder] Could not load bike database:', err);
        self.database = { bikes: [], standards: {} };
        return self.database;
      });
    return this.databasePromise;
  };

  BBFinder.prototype.matchesCategory = function (bike) {
    return this.category === 'all' || bike.category === this.category;
  };

  BBFinder.prototype.renderBrandChips = function () {
    if (!this.brandChips || !this.database || !this.database.bikes.length) return;
    var self = this;
    var counts = {};
    this.database.bikes.forEach(function (b) {
      if (!self.matchesCategory(b)) return;
      counts[b.brand] = (counts[b.brand] || 0) + 1;
    });
    var brands = Object.keys(counts)
      .sort(function (a, b) { return counts[b] - counts[a]; })
      .slice(0, MAX_BRAND_CHIPS);

    this.brandChips.innerHTML = '';
    brands.forEach(function (brand) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'qo-bbf__brand-chip';
      chip.textContent = brand;
      chip.addEventListener('click', function () {
        self.searchInput.value = brand + ' ';
        self.searchInput.focus();
        self.runSearch(brand);
      });
      self.brandChips.appendChild(chip);
    });
    this.brandsWrap.hidden = brands.length === 0;
  };

  BBFinder.prototype.bindCategoryFilter = function () {
    var self = this;
    var pills = this.root.querySelectorAll('[data-bbf-cat]');
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        self.category = pill.dataset.bbfCat;
        pills.forEach(function (p) {
          var active = p === pill;
          p.classList.toggle('is-active', active);
          p.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        self.renderBrandChips();
        if (self.searchInput && self.searchInput.value.trim().length >= 2) {
          self.runSearch(self.searchInput.value);
        } else {
          self.renderSearchResults([]);
          self.noResults.hidden = true;
        }
      });
    });
  };

  /* ---------------- Search ---------------- */

  BBFinder.prototype.bindSearch = function () {
    if (!this.searchInput) return;
    var self = this;
    this.searchInput.addEventListener('input', debounce(function () {
      self.runSearch(self.searchInput.value);
    }, 120));
  };

  BBFinder.prototype.runSearch = function (query) {
    var self = this;
    var q = normalize(query);
    if (q.length < 2) {
      this.renderSearchResults([]);
      this.noResults.hidden = true;
      return;
    }
    this.loadDatabase().then(function () {
      var tokens = q.split(' ');
      var matches = self.database.bikes
        .map(function (bike) {
          if (!self.matchesCategory(bike)) return null;
          var haystack = normalize(bike.brand + ' ' + bike.model + ' ' + (bike.years || ''));
          var hit = tokens.every(function (tok) { return haystack.indexOf(tok) !== -1; });
          if (!hit) return null;
          // Rank brand-prefix matches first
          var score = normalize(bike.brand).indexOf(tokens[0]) === 0 ? 0 : 1;
          return { bike: bike, score: score };
        })
        .filter(Boolean)
        .sort(function (a, b) { return a.score - b.score; })
        .slice(0, MAX_RESULTS)
        .map(function (m) { return m.bike; });

      self.renderSearchResults(matches);
      self.noResults.hidden = matches.length > 0;
    });
  };

  BBFinder.prototype.renderSearchResults = function (bikes) {
    var self = this;
    this.resultsList.innerHTML = '';
    bikes.forEach(function (bike) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'qo-bbf__result-item';
      btn.setAttribute('role', 'option');

      var name = document.createElement('span');
      name.className = 'qo-bbf__result-name';
      name.textContent = bike.brand + ' ' + bike.model;

      var years = document.createElement('span');
      years.className = 'qo-bbf__result-years';
      years.textContent = bike.years || '';

      btn.appendChild(name);
      btn.appendChild(years);
      btn.addEventListener('click', function () {
        var context = bike.brand + ' ' + bike.model + (bike.years ? ' (' + bike.years + ')' : '');
        self.showResult(bike.bb, context, 'search', bike.note);
      });

      li.appendChild(btn);
      self.resultsList.appendChild(li);
    });
  };

  /* ---------------- Tabs & wizard ---------------- */

  BBFinder.prototype.bindTabs = function () {
    var self = this;
    this.root.querySelectorAll('[data-bbf-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.activateTab(tab.dataset.bbfTab);
      });
    });
    this.root.querySelectorAll('[data-bbf-goto-wizard]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.activateTab('wizard'); });
    });
    this.root.querySelectorAll('[data-bbf-goto-search]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.activateTab('search');
        if (self.searchInput) self.searchInput.focus();
      });
    });
  };

  BBFinder.prototype.activateTab = function (name) {
    this.root.querySelectorAll('[data-bbf-tab]').forEach(function (tab) {
      var active = tab.dataset.bbfTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.root.querySelectorAll('[data-bbf-panel]').forEach(function (panel) {
      var active = panel.dataset.bbfPanel === name;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
    this.hideResult();
    if (name === 'wizard') this.gotoStep('start');
  };

  BBFinder.prototype.bindWizard = function () {
    var self = this;
    this.root.querySelectorAll('[data-bbf-goto]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.gotoStep(btn.dataset.bbfGoto);
      });
    });
  };

  BBFinder.prototype.gotoStep = function (name) {
    this.root.querySelectorAll('[data-bbf-step]').forEach(function (step) {
      var active = step.dataset.bbfStep === name;
      step.classList.toggle('is-active', active);
      step.hidden = !active;
    });
    this.hideResult();
  };

  BBFinder.prototype.bindStandardButtons = function () {
    var self = this;
    this.root.querySelectorAll('[data-bbf-result]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var source = btn.closest('[data-bbf-panel="wizard"]') ? 'wizard' : 'standards';
        self.showResult(btn.dataset.bbfResult, '', source, null);
      });
    });
  };

  /* ---------------- Result ---------------- */

  BBFinder.prototype.showResult = function (standardKey, contextText, source, extraNote) {
    // Keys are lowercase by convention; normalize to survive hand-edited data
    standardKey = (standardKey || '').toLowerCase();
    var template = this.root.querySelector('template[data-bbf-template="' + CSS.escape(standardKey) + '"]');
    var isFallback = false;
    if (!template) {
      template = this.root.querySelector('template[data-bbf-template-fallback]');
      isFallback = true;
    }
    if (!template) return;

    this.resultArea.innerHTML = '';
    this.resultArea.appendChild(template.content.cloneNode(true));
    this.resultArea.hidden = false;

    var contextEl = this.resultArea.querySelector('[data-bbf-context]');
    if (contextEl) {
      var label = this.standardLabel(standardKey);
      var parts = [];
      if (contextText) parts.push(contextText);
      if (isFallback && label) parts.push(label);
      contextEl.textContent = parts.join(' — ');
      contextEl.hidden = parts.length === 0;
    }

    if (extraNote) {
      var card = this.resultArea.querySelector('.qo-bbf__card');
      if (card) {
        var noteEl = document.createElement('p');
        noteEl.className = 'qo-bbf__card-note qo-bbf__card-note--data';
        noteEl.textContent = extraNote;
        card.appendChild(noteEl);
      }
    }

    this.bindVariants();
    this.bindAddToCart();
    if (this.machine) this.machine.classList.add('is-result');
    this.resultArea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    trackResult({ standard: standardKey, bike: contextText || null, source: source });
  };

  BBFinder.prototype.hideResult = function () {
    this.resultArea.hidden = true;
    this.resultArea.innerHTML = '';
    if (this.machine) this.machine.classList.remove('is-result');
  };

  BBFinder.prototype.standardLabel = function (key) {
    if (this.database && this.database.standards && this.database.standards[key]) {
      return this.database.standards[key];
    }
    return key;
  };

  /* ---------------- Variant selector (ceramic / steel bearings) ---------------- */

  BBFinder.prototype.bindVariants = function () {
    var self = this;
    var group = this.resultArea.querySelector('[data-bbf-variants]');
    if (!group) return;
    var addBtn = this.resultArea.querySelector('[data-bbf-add-to-cart]');
    var priceEl = this.resultArea.querySelector('[data-bbf-price]');
    var compareEl = this.resultArea.querySelector('[data-bbf-compare]');
    group.querySelectorAll('[data-bbf-variant]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        self.selectVariant(opt, group, addBtn, priceEl, compareEl);
      });
    });
  };

  BBFinder.prototype.selectVariant = function (opt, group, addBtn, priceEl, compareEl) {
    group.querySelectorAll('[data-bbf-variant]').forEach(function (o) {
      var on = o === opt;
      o.classList.toggle('is-selected', on);
      o.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    if (priceEl && opt.dataset.variantPrice) priceEl.textContent = opt.dataset.variantPrice;
    if (compareEl) {
      var cmp = opt.dataset.variantCompare;
      if (cmp) { compareEl.textContent = cmp; compareEl.hidden = false; }
      else { compareEl.textContent = ''; compareEl.hidden = true; }
    }

    if (addBtn) {
      addBtn.dataset.bbfAddToCart = opt.dataset.bbfVariant;
      var available = opt.dataset.variantAvailable === 'true';
      addBtn.disabled = !available;
      addBtn.setAttribute('aria-disabled', available ? 'false' : 'true');
      addBtn.textContent = available
        ? (addBtn.dataset.labelAdd || addBtn.textContent)
        : (addBtn.dataset.labelSoldout || addBtn.textContent);
    }
  };

  /* ---------------- Add to cart (Dawn drawer aware) ---------------- */

  BBFinder.prototype.bindAddToCart = function () {
    var self = this;
    this.resultArea.querySelectorAll('[data-bbf-add-to-cart]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.addToCart(btn);
      });
    });
  };

  BBFinder.prototype.addToCart = function (btn) {
    var self = this;
    var variantId = btn.dataset.bbfAddToCart;
    if (!variantId || btn.disabled) return;

    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = btn.dataset.labelAdding || originalLabel;

    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    var body = { id: Number(variantId), quantity: 1 };
    var canRenderDrawer = cart &&
      typeof cart.getSectionsToRender === 'function' &&
      typeof cart.renderContents === 'function';

    if (canRenderDrawer) {
      body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }

    fetch(this.cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
      .then(function (res) {
        if (!res.ok || res.json.status) throw new Error(res.json.description || 'Cart error');
        if (canRenderDrawer) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(res.json);
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'qo-bb-finder', cartData: res.json });
          }
        } else {
          window.location.href = self.cartUrl;
          return;
        }
        btn.textContent = btn.dataset.labelAdded || originalLabel;
        setTimeout(function () {
          btn.textContent = originalLabel;
          btn.disabled = false;
        }, 2500);
      })
      .catch(function (err) {
        console.warn('[QO BB Finder] Add to cart failed:', err);
        btn.textContent = btn.dataset.labelError || originalLabel;
        setTimeout(function () {
          btn.textContent = originalLabel;
          btn.disabled = false;
        }, 2500);
      });
  };

  /* ---------------- Boot ---------------- */

  function init() {
    document.querySelectorAll('[data-qo-bbf]').forEach(function (root) {
      if (!root.dataset.bbfReady) {
        root.dataset.bbfReady = 'true';
        new BBFinder(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the merchant edits the section in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
